(() => {
  'use strict';

  const PAGE_SIZE = 6;
  const REMOTE_PAGE_SIZE = 150;
  const MAX_REMOTE_PAGES = 4;
  const YOTPO_HOST = 'api-cdn.yotpo.com';
  const REQUEST_TIMEOUT_MS = 8000;

  function decodeHtmlEntities(value) {
    const named = { amp: '&', apos: "'", gt: '>', lt: '<', nbsp: ' ', quot: '"' };
    return String(value ?? '').replace(/&(#x[0-9a-f]+|#\d+|amp|apos|gt|lt|nbsp|quot);/gi, (entity, token) => {
      if (named[token.toLowerCase()]) return named[token.toLowerCase()];
      const codePoint = token[1].toLowerCase() === 'x' ? Number.parseInt(token.slice(2), 16) : Number.parseInt(token.slice(1), 10);
      return Number.isInteger(codePoint) && codePoint >= 0 && codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : entity;
    });
  }

  function commentText(review) {
    if (typeof review.comment === 'string') return decodeHtmlEntities(review.comment).trim();
    if (review.comment && typeof review.comment === 'object') return decodeHtmlEntities(review.comment.content || review.comment.comment || '').trim();
    return '';
  }

  function isReview(review) {
    return review && Number.isInteger(review.id) && Number.isInteger(review.score) && review.score >= 1 && review.score <= 5 && !review.deleted && typeof review.content === 'string' && review.content.trim() && typeof review.created_at === 'string' && !Number.isNaN(Date.parse(review.created_at)) && typeof review.user?.display_name === 'string' && review.user.display_name.trim() && (!review.images_data || (Array.isArray(review.images_data) && review.images_data.every(image => /^https:\/\/cdn-yotpo-images-production\.yotpo.com\//.test(image?.original_url || ''))));
  }

  function normalizeLivePayload(raw, expectedProductId) {
    const envelope = raw?.response ? raw : { status: { code: 200 }, response: raw };
    const response = envelope.response;
    const reviews = response?.reviews;
    const total = Number(response?.pagination?.total);
    const product = response?.products?.find(item => String(item.domain_key) === String(expectedProductId));
    const average = Number(response?.bottomline?.average_score);
    if (envelope?.status?.code !== 200 || !response || !Array.isArray(reviews) || !Number.isInteger(total) || total < 1 || response?.bottomline?.total_review !== total || reviews.length !== total || !product?.id || !Number.isFinite(average) || average < 1 || average > 5) return null;
    const ids = new Set(reviews.map(review => review?.id));
    const mean = reviews.reduce((sum, review) => sum + review.score, 0) / reviews.length;
    if (ids.size !== reviews.length || Math.abs(mean - average) > 0.02 || reviews.some(review => !isReview(review) || String(review.product_id) !== String(product.id))) return null;
    return { reviews, bottomline: response.bottomline, total };
  }

  function pageUrl(baseUrl, page) {
    const url = new URL(baseUrl);
    const productId = url.pathname.match(/^\/v1\/widget\/[^/]+\/products\/(\d+)\/reviews\.json$/)?.[1];
    if (url.protocol !== 'https:' || url.hostname !== YOTPO_HOST || !productId) return null;
    url.searchParams.set('per_page', String(REMOTE_PAGE_SIZE));
    url.searchParams.set('page', String(page));
    return { url, productId };
  }

  async function fetchCompleteReviews(baseUrl, expectedProductId, requestFetch = fetch) {
    const first = pageUrl(baseUrl, 1);
    if (!first || first.productId !== String(expectedProductId)) return null;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const request = async page => {
      const target = pageUrl(baseUrl, page);
      if (!target) return null;
      const response = await requestFetch(target.url, { credentials: 'omit', cache: 'no-store', signal: controller.signal });
      if (!response.ok) return null;
      const payload = await response.json();
      const body = payload?.response || payload;
      if (payload?.status?.code && payload.status.code !== 200 || body?.pagination?.page !== page || !body?.products?.some(product => String(product.domain_key) === String(expectedProductId))) return null;
      return payload;
    };
    try {
      const firstPayload = await request(1);
      const firstResponse = firstPayload?.response || firstPayload;
      const total = Number(firstResponse?.pagination?.total);
      const pages = Math.ceil(total / REMOTE_PAGE_SIZE);
      if (!Number.isInteger(total) || total < 1 || pages < 1 || pages > MAX_REMOTE_PAGES) return null;
      const payloads = [firstPayload];
      for (let page = 2; page <= pages; page += 1) payloads.push(await request(page));
      if (payloads.some(payload => !payload || Number((payload.response || payload).pagination?.total) !== total)) return null;
      const reviews = payloads.flatMap(payload => (payload.response || payload).reviews || []);
      const canonical = { status: { code: 200 }, response: { ...firstResponse, pagination: { ...firstResponse.pagination, total }, reviews } };
      return normalizeLivePayload(canonical, expectedProductId);
    } finally {
      clearTimeout(timeout);
    }
  }

  function makeElement(name, className, text) {
    const element = document.createElement(name);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function formatDate(value) {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(value));
  }

  function formatHktTimestamp(value) {
    return `${new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Hong_Kong' }).format(new Date(value))} HKT`;
  }

  function appendStars(parent, score) {
    for (let index = 0; index < 5; index += 1) parent.append(makeElement('span', `sereni-review__star${index < score ? ' is-filled' : ''}`, String.fromCharCode(9733)));
  }

  function photoButton(image, alt, className) {
    const button = makeElement('button', className);
    button.type = 'button';
    button.setAttribute('aria-label', `Open ${alt}`);
    button.dataset.reviewPhotoSrc = image.original_url;
    button.dataset.reviewPhotoAlt = alt;
    const picture = document.createElement('img');
    picture.src = image.original_url;
    picture.alt = '';
    picture.width = 640;
    picture.height = 640;
    picture.loading = 'lazy';
    const frame = makeElement('span', 'sereni-review-photo__frame');
    frame.append(picture);
    button.append(frame);
    return button;
  }

  function mapReviewPhotos(live, manifest, productId) {
    const entries = String(manifest?.productId) === String(productId) ? manifest.entries || {} : {};
    return { ...live, reviews: live.reviews.map(review => ({ ...review,
      images_data: (review.images_data || []).flatMap(image => {
        const url = entries[image.original_url]?.url;
        try {
          const parsed = new URL(url);
          if (parsed.protocol === 'https:' && parsed.hostname === 'cdn.shopify.com') return [{ ...image, original_url: url }];
        } catch {}
        return [];
      })
    })) };
  }

  async function fetchMediaMap(root) {
    const url = new URL(root.dataset.reviewMediaUrl, window.location.href);
    if (url.protocol !== 'https:' || ![window.location.hostname, 'cdn.shopify.com'].includes(url.hostname)) return null;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, { credentials: 'omit', cache: 'no-store', signal: controller.signal });
      if (!response.ok) return null;
      return await response.json();
    } finally { clearTimeout(timeout); }
  }

  function reviewCard(review) {
    const card = makeElement('article', 'sereni-review');
    card.dataset.reviewCard = '';
    card.dataset.rating = String(review.score);
    card.dataset.createdAt = review.created_at;
    const header = makeElement('header', 'sereni-review__header');
    const intro = document.createElement('div');
    const rating = makeElement('p', 'sereni-review__rating');
    rating.setAttribute('aria-label', `${review.score} out of 5 stars`);
    appendStars(rating, review.score);
    intro.append(rating);
    if (review.title) intro.append(makeElement('h3', '', decodeHtmlEntities(review.title)));
    const meta = makeElement('p', 'sereni-review__meta');
    const name = decodeHtmlEntities(review.user.display_name);
    meta.append(makeElement('strong', '', name));
    if (review.verified_buyer === true) meta.append(makeElement('span', 'sereni-review__verified', 'Verified buyer'));
    if (review.is_incentivized === true) meta.append(makeElement('span', 'sereni-review__incentivized', 'Incentivized review'));
    const time = makeElement('time', '', formatDate(review.created_at));
    time.dateTime = review.created_at;
    meta.append(time);
    header.append(intro, meta);
    card.append(header, makeElement('p', 'sereni-review__body', decodeHtmlEntities(review.content)));
    const comment = commentText(review);
    if (comment) {
      const response = makeElement('div', 'sereni-review__comment');
      response.append(makeElement('strong', '', 'Response'), makeElement('p', '', comment));
      card.append(response);
    }
    const images = review.images_data || [];
    if (images.length) {
      const photos = makeElement('div', 'sereni-review__photos');
      images.forEach((image, index) => photos.append(photoButton(image, `Photo ${index + 1} shared with ${name}'s Sereni review`, 'sereni-review__photo')));
      card.append(photos);
    }
    return card;
  }

  function init(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const snapshot = root.querySelector('[data-review-snapshot]');
    if (!snapshot) return;
    const list = snapshot.querySelector('[data-review-list]');
    const filter = snapshot.querySelector('[data-review-filter]');
    const sort = snapshot.querySelector('[data-review-sort]');
    const results = snapshot.querySelector('[data-review-results]');
    const loadMore = snapshot.querySelector('[data-review-load-more]');
    const rail = snapshot.querySelector('.sereni-review-photos__rail');
    const photoSection = snapshot.querySelector('.sereni-review-photos');
    const summaryScore = snapshot.querySelector('[data-review-summary-score]');
    const summaryCount = snapshot.querySelector('[data-review-summary-count]');
    const freshness = snapshot.querySelector('[data-review-snapshot-time]')?.parentElement;
    const lightbox = root.querySelector('[data-review-lightbox]');
    const lightboxImage = root.querySelector('[data-review-lightbox-image]');
    const lightboxCaption = root.querySelector('[data-review-lightbox-caption]');
    const closeLightbox = root.querySelector('[data-review-lightbox-close]');
    let cards = [...list.querySelectorAll('[data-review-card]')];
    let displayed = PAGE_SIZE;
    let updatePhotoNavigation = () => {};

    snapshot.querySelectorAll('[data-review-photo-src] > img').forEach(picture => {
      const frame = makeElement('span', 'sereni-review-photo__frame');
      picture.before(frame);
      frame.append(picture);
    });
    if (rail && photoSection) {
      rail.id = 'sereni-review-photo-rail';
      const controls = makeElement('div', 'sereni-review-photos__navigation');
      const previous = makeElement('button', '', '\u2190');
      const next = makeElement('button', '', '\u2192');
      [previous, next].forEach((button, index) => {
        button.type = 'button';
        button.title = index ? 'Next customer photos' : 'Previous customer photos';
        button.setAttribute('aria-label', button.title);
        button.setAttribute('aria-controls', rail.id);
        button.addEventListener('click', () => rail.scrollBy({left: (index ? 1 : -1) * rail.clientWidth * .8, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'}));
      });
      controls.append(previous, next);
      photoSection.append(controls);
      const updateNavigation = updatePhotoNavigation = () => {
        previous.disabled = rail.scrollLeft <= 1;
        next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
      };
      rail.addEventListener('scroll', updateNavigation, {passive:true});
      if (window.ResizeObserver) new ResizeObserver(updateNavigation).observe(rail);
      updateNavigation();
    }

    function syncPriceReviewLink() {
      const price = document.querySelector('.sereni-editorial__price');
      if (!price) return;
      let jump = document.querySelector('.sereni-editorial__review-link');
      if (!jump) {
        jump = document.createElement('a');
        jump.className = 'sereni-editorial__review-link';
        jump.href = '#sereni-reviews';
        price.after(jump);
      }
      const average = Number(snapshot.dataset.averageScore);
      const count = Number(snapshot.dataset.reviewCount);
      jump.hidden = !Number.isFinite(average) || average < 0 || average > 5 || !Number.isFinite(count) || count < 1;
      if (jump.hidden) return;
      const stars = document.createElement('span');
      stars.className = 'sereni-editorial__review-stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${average} out of 5 stars`);
      const track = document.createElement('span');
      track.setAttribute('aria-hidden', 'true');
      track.textContent = '\u2605\u2605\u2605\u2605\u2605';
      const fill = track.cloneNode(true);
      fill.className = 'sereni-editorial__review-stars-fill';
      fill.style.width = `${average / 5 * 100}%`;
      stars.append(track, fill);
      jump.replaceChildren(stars, `${average.toFixed(1)} (${count} reviews) · View reviews`);
    }

    function updateReviews() {
      const selectedRating = filter.value;
      const selectedSort = sort.value;
      const matching = cards.filter(card => selectedRating === 'all' || card.dataset.rating === selectedRating);
      matching.sort((first, second) => {
        if (selectedSort === 'highest') return Number(second.dataset.rating) - Number(first.dataset.rating) || Date.parse(second.dataset.createdAt) - Date.parse(first.dataset.createdAt);
        if (selectedSort === 'lowest') return Number(first.dataset.rating) - Number(second.dataset.rating) || Date.parse(second.dataset.createdAt) - Date.parse(first.dataset.createdAt);
        return Date.parse(second.dataset.createdAt) - Date.parse(first.dataset.createdAt);
      });
      cards.forEach(card => { card.hidden = true; });
      matching.forEach((card, index) => { card.hidden = index >= displayed; list.append(card); });
      results.textContent = `Showing ${Math.min(displayed, matching.length)} of ${matching.length} review${matching.length === 1 ? '' : 's'}`;
      loadMore.hidden = matching.length <= displayed;
    }

    function rebuildPhotoRail(reviews) {
      if (!rail || !photoSection) return;
      const fragment = document.createDocumentFragment();
      let count = 0;
      reviews.forEach(review => (review.images_data || []).forEach(image => {
        count += 1;
        const name = decodeHtmlEntities(review.user.display_name);
        const button = photoButton(image, `Photo ${count} shared with ${name}'s Sereni review`, 'sereni-review-photos__item');
        button.append(makeElement('span', '', name));
        fragment.append(button);
      }));
      rail.replaceChildren(fragment);
      photoSection.hidden = count === 0;
      updatePhotoNavigation();
    }

    function renderLiveReviews(live) {
      cards = live.reviews.map(reviewCard);
      list.replaceChildren(...cards);
      snapshot.dataset.reviewCount = String(live.total);
      snapshot.dataset.averageScore = Number(live.bottomline.average_score).toFixed(2);
      summaryScore.textContent = Number(live.bottomline.average_score).toFixed(2);
      summaryCount.textContent = `${live.total} published reviews`;
      const checkedAt = new Date();
      const time = document.createElement('time');
      time.dateTime = checkedAt.toISOString();
      time.textContent = formatHktTimestamp(checkedAt.toISOString());
      freshness.replaceChildren('Live Yotpo response checked ', time, '.');
      rebuildPhotoRail(live.reviews);
      displayed = PAGE_SIZE;
      syncPriceReviewLink();
      updateReviews();
    }

    function openPhoto(button) {
      if (!lightbox || !lightboxImage || !window.HTMLDialogElement) return;
      lightboxImage.src = button.dataset.reviewPhotoSrc;
      lightboxImage.alt = button.dataset.reviewPhotoAlt || 'Customer photo';
      lightboxCaption.textContent = button.dataset.reviewPhotoAlt || 'Customer photo';
      lightbox.showModal();
      closeLightbox.focus();
    }

    filter.addEventListener('change', () => { displayed = PAGE_SIZE; updateReviews(); });
    sort.addEventListener('change', () => { displayed = PAGE_SIZE; updateReviews(); });
    loadMore.addEventListener('click', () => { displayed += PAGE_SIZE; updateReviews(); });
    root.addEventListener('click', event => {
      const photo = event.target.closest('[data-review-photo-src]');
      if (photo) openPhoto(photo);
    });
    closeLightbox?.addEventListener('click', () => lightbox.close());
    lightbox?.addEventListener('click', event => { if (event.target === lightbox) lightbox.close(); });
    syncPriceReviewLink();
    updateReviews();
    Promise.all([
      fetchCompleteReviews(snapshot.dataset.yotpoReviewsUrl, snapshot.dataset.productId),
      fetchMediaMap(root)
    ]).then(([live, media]) => {
      if (live) renderLiveReviews(mapReviewPhotos(live, media, snapshot.dataset.productId));
    }).catch(() => {});
  }

  if (typeof module !== 'undefined') module.exports = { decodeHtmlEntities, fetchCompleteReviews, normalizeLivePayload, mapReviewPhotos };
  if (typeof document === 'undefined') return;
  document.querySelectorAll('.sereni-reviews').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('.sereni-reviews').forEach(init));
})();
